(() => {
  'use strict';

  const root =
    document.querySelector(
      '[data-pnmatch-root]'
    );

  if (!root) return;

  const form =
    root.querySelector(
      '[data-pnmatch-form]'
    );

  const status =
    root.querySelector(
      '[data-pnmatch-status]'
    );

  const steps =
    [...root.querySelectorAll('[data-pnmatch-step]')];

  const dots =
    [...root.querySelectorAll('[data-pnmatch-dot]')];

  const previousButton =
    root.querySelector('[data-pnmatch-prev]');

  const nextButton =
    root.querySelector('[data-pnmatch-next]');

  const saveButton =
    root.querySelector('[data-pnmatch-save]');

  if (!form || !steps.length) return;

  let currentStep = 0;
  let started = false;
  let preferences = null;
  let courses = [];
  let colleges = [];

  const clean = value =>
    value === null ||
    value === undefined
      ? ''
      : String(value).trim();

  const escapeHTML = value =>
    clean(value)
      .replace(/&/g,'&amp;')
      .replace(/</g,'&lt;')
      .replace(/>/g,'&gt;')
      .replace(/"/g,'&quot;')
      .replace(/'/g,'&#039;');

  const unique = values =>
    [...new Set(
      values
        .map(clean)
        .filter(Boolean)
    )].sort((a,b) => a.localeCompare(b));

  const courseName = course =>
    clean(
      course?.name ||
      course?.title ||
      course?.course_name ||
      course?.slug ||
      course?.id
    );

  function setStatus(message,state='') {
    if (!status) return;
    status.textContent = message || '';
    status.dataset.state = state;
  }

  function showStep(index) {
    currentStep =
      Math.max(
        0,
        Math.min(index,steps.length - 1)
      );

    steps.forEach((step,i) => {
      step.hidden = i !== currentStep;
    });

    dots.forEach((dot,i) => {
      dot.classList.toggle(
        'is-active',
        i === currentStep
      );

      dot.classList.toggle(
        'is-complete',
        i < currentStep
      );
    });

    if (previousButton) {
      previousButton.hidden =
        currentStep === 0;
    }

    if (nextButton) {
      nextButton.hidden =
        currentStep === steps.length - 1;
    }

    if (saveButton) {
      saveButton.hidden =
        currentStep !== steps.length - 1;
    }
  }

  function numberValue(name) {
    const input = form.elements[name];
    const raw = clean(input?.value);

    if (!raw) return null;

    const value = Number(raw);

    return Number.isFinite(value)
      ? value
      : null;
  }

  function integerValue(name,fallback) {
    const value = numberValue(name);

    return Number.isFinite(value)
      ? Math.round(value)
      : fallback;
  }

  function checkedValues(name) {
    return [
      ...form.querySelectorAll(
        `[name="${name}"]:checked`
      )
    ].map(input => input.value);
  }

  function payload() {
    return {
      preferred_course_id:
        clean(
          form.elements
            .preferred_course_id
            ?.value
        ) || null,

      preferred_department:
        clean(
          form.elements
            .preferred_department
            ?.value
        ) || null,

      preferred_specialization:
        clean(
          form.elements
            .preferred_specialization
            ?.value
        ) || null,

      strict_course_match:
        Boolean(
          form.elements
            .strict_course_match
            ?.checked
        ),

      preferred_cities:
        checkedValues(
          'preferred_cities'
        ),

      preferred_states:
        checkedValues(
          'preferred_states'
        ),

      preferred_country:
        'India',

      max_annual_fee:
        numberValue(
          'max_annual_fee'
        ),

      strict_budget:
        Boolean(
          form.elements
            .strict_budget
            ?.checked
        ),

      preferred_college_types:
        checkedValues(
          'preferred_college_types'
        ),

      preferred_ownership_types:
        checkedValues(
          'preferred_ownership_types'
        ),

      min_placement_rate:
        numberValue(
          'min_placement_rate'
        ),

      min_average_package:
        numberValue(
          'min_average_package'
        ),

      placement_currency:
        'INR',

      course_priority:
        integerValue(
          'course_priority',
          5
        ),

      budget_priority:
        integerValue(
          'budget_priority',
          4
        ),

      location_priority:
        integerValue(
          'location_priority',
          3
        ),

      placements_priority:
        integerValue(
          'placements_priority',
          4
        ),

      college_type_priority:
        integerValue(
          'college_type_priority',
          2
        ),

      ownership_priority:
        integerValue(
          'ownership_priority',
          2
        ),

      campus_life_preferences:
        clean(
          form.elements
            .campus_life_preferences
            ?.value
        ) || null,

      student_culture_preferences:
        clean(
          form.elements
            .student_culture_preferences
            ?.value
        ) || null,

      additional_preferences:
        clean(
          form.elements
            .additional_preferences
            ?.value
        ) || null
    };
  }

  function setValue(name,value) {
    const input = form.elements[name];

    if (!input) return;

    input.value =
      value === null ||
      value === undefined
        ? ''
        : value;
  }

  function setChecked(name,value) {
    const input = form.elements[name];

    if (!input) return;

    input.checked =
      Boolean(value);
  }

  function renderChecks(
    key,
    name,
    values,
    selected
  ) {
    const container =
      root.querySelector(
        `[data-pnmatch-options="${key}"]`
      );

    if (!container) return;

    if (!values.length) {
      container.innerHTML =
        '<span>No published options yet — leave unrestricted.</span>';
      return;
    }

    const selectedSet =
      new Set(
        Array.isArray(selected)
          ? selected.map(clean)
          : []
      );

    container.innerHTML =
      values.map(value => `
        <label class="pnmatch-chip">
          <input
            type="checkbox"
            name="${escapeHTML(name)}"
            value="${escapeHTML(value)}"
            ${selectedSet.has(value) ? 'checked' : ''}
          >
          <span>${escapeHTML(value)}</span>
        </label>
      `).join('');
  }

  function populate() {
    const courseSelect =
      form.elements.preferred_course_id;

    const selectedCourse =
      clean(
        preferences?.preferred_course_id
      );

    const usable =
      courses
        .filter(
          item =>
            item?.id &&
            courseName(item)
        )
        .sort(
          (a,b) =>
            courseName(a)
              .localeCompare(
                courseName(b)
              )
        );

    courseSelect.innerHTML = [
      '<option value="">Choose a course</option>',
      ...usable.map(item => `
        <option
          value="${escapeHTML(item.id)}"
          ${String(item.id) === selectedCourse ? 'selected' : ''}
        >
          ${escapeHTML(courseName(item))}
        </option>
      `)
    ].join('');

    setValue(
      'preferred_department',
      preferences?.preferred_department
    );

    setValue(
      'preferred_specialization',
      preferences?.preferred_specialization
    );

    setChecked(
      'strict_course_match',
      preferences?.strict_course_match ?? true
    );

    setValue(
      'max_annual_fee',
      preferences?.max_annual_fee
    );

    setChecked(
      'strict_budget',
      preferences?.strict_budget ?? false
    );

    setValue(
      'min_placement_rate',
      preferences?.min_placement_rate
    );

    setValue(
      'min_average_package',
      preferences?.min_average_package
    );

    const priorities = [
      ['course_priority',5],
      ['budget_priority',4],
      ['location_priority',3],
      ['placements_priority',4],
      ['college_type_priority',2],
      ['ownership_priority',2]
    ];

    priorities.forEach(([name,fallback]) => {
      const value =
        preferences?.[name] ?? fallback;

      setValue(name,value);

      const output =
        form.querySelector(
          `[data-pnmatch-range="${name}"]`
        );

      if (output) {
        output.textContent = value;
      }
    });

    setValue(
      'campus_life_preferences',
      preferences?.campus_life_preferences
    );

    setValue(
      'student_culture_preferences',
      preferences?.student_culture_preferences
    );

    setValue(
      'additional_preferences',
      preferences?.additional_preferences
    );

    renderChecks(
      'cities',
      'preferred_cities',
      unique(colleges.map(x => x.city)),
      preferences?.preferred_cities
    );

    renderChecks(
      'states',
      'preferred_states',
      unique(colleges.map(x => x.state)),
      preferences?.preferred_states
    );

    renderChecks(
      'college-types',
      'preferred_college_types',
      unique(colleges.map(x => x.college_type)),
      preferences?.preferred_college_types
    );

    renderChecks(
      'ownership-types',
      'preferred_ownership_types',
      unique(colleges.map(x => x.ownership_type)),
      preferences?.preferred_ownership_types
    );

    setStatus(
      preferences?.completed_at
        ? 'Your saved Match preferences are loaded.'
        : 'Set your preferences once. You can update them anytime.',
      preferences?.completed_at
        ? 'saved'
        : ''
    );
  }

  function validateStep() {
    if (currentStep !== 0) {
      return true;
    }

    const course =
      clean(
        form.elements
          .preferred_course_id
          ?.value
      );

    if (course) {
      return true;
    }

    setStatus(
      'Choose a course before continuing.',
      'error'
    );

    form.elements
      .preferred_course_id
      ?.focus();

    return false;
  }

  async function save() {
    const data = payload();

    if (!data.preferred_course_id) {
      showStep(0);

      setStatus(
        'Choose a course before saving.',
        'error'
      );

      return;
    }

    if (saveButton) {
      saveButton.disabled = true;
      saveButton.textContent = 'Saving…';
    }

    try {
      preferences =
        await window.PeerNestDB.request(
          'rpc/save_my_match_preferences',
          '',
          {
            method: 'POST',
            body: {
              p_preferences: data,
              p_mark_complete: true
            }
          }
        );

      setStatus(
        'Preferences saved. Your profile is ready for College Match.',
        'saved'
      );
    }
    catch (error) {
      console.error(
        '[PeerNest Match Setup]',
        error
      );

      setStatus(
        error?.message ||
        'Could not save your Match preferences.',
        'error'
      );
    }
    finally {
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.textContent =
          'Save Match Preferences';
      }
    }
  }

  async function load() {
    if (started || !window.PeerNestDB) {
      return;
    }

    started = true;

    setStatus(
      'Loading your Match setup…'
    );

    const results =
      await Promise.allSettled([
        window.PeerNestDB.request(
          'rpc/get_my_match_preferences',
          '',
          {
            method: 'POST',
            body: {}
          }
        ),

        window.PeerNestDB.request(
          'courses',
          'select=*'
        ),

        window.PeerNestDB
          .getPublishedColleges()
      ]);

    preferences =
      results[0].status === 'fulfilled'
        ? results[0].value
        : null;

    courses =
      results[1].status === 'fulfilled' &&
      Array.isArray(results[1].value)
        ? results[1].value
        : [];

    colleges =
      results[2].status === 'fulfilled' &&
      Array.isArray(results[2].value)
        ? results[2].value
        : [];

    populate();
    showStep(0);
  }

  previousButton?.addEventListener(
    'click',
    () => {
      setStatus('');
      showStep(currentStep - 1);
    }
  );

  nextButton?.addEventListener(
    'click',
    () => {
      if (!validateStep()) return;

      setStatus('');
      showStep(currentStep + 1);
    }
  );

  saveButton?.addEventListener(
    'click',
    save
  );

  form.addEventListener(
    'input',
    event => {
      if (event.target?.type !== 'range') {
        return;
      }

      const output =
        form.querySelector(
          `[data-pnmatch-range="${event.target.name}"]`
        );

      if (output) {
        output.textContent =
          event.target.value;
      }
    }
  );

  if (window.PeerNestDB) {
    load();
  }
  else {
    document.addEventListener(
      'peernest:supabase-ready',
      load,
      { once:true }
    );
  }
})();
